/** Formatos compartidos de la consola (es-ES). */

export function fmtMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency,
    useGrouping: 'always',
  }).format(cents / 100);
}

const dateFmt = new Intl.DateTimeFormat('es-ES', { dateStyle: 'medium', timeZone: 'UTC' });

/** `2026-09-22` (o ISO) → `22 sept 2026`. */
export function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(d.getTime()) ? '—' : dateFmt.format(d);
}

/** Tiempo relativo corto: "ahora mismo", "hace 5 min", "hace 3 h", "ayer", "hace 4 días". */
export function fmtAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '—';
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  if (s < 60) return 'ahora mismo';
  if (s < 3600) return `hace ${String(Math.round(s / 60))} min`;
  if (s < 86_400) return `hace ${String(Math.round(s / 3600))} h`;
  const d = Math.round(s / 86_400);
  if (d === 1) return 'ayer';
  if (d < 45) return `hace ${String(d)} días`;
  return fmtDate(iso);
}

const shortDate = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short',
  timeZone: 'UTC',
});

/** `2026-08-24`, `2026-09-22` → `24 ago – 22 sept`. */
export function fmtRange(from: string, to: string): string {
  const f = (v: string) => shortDate.format(new Date(`${v}T00:00:00Z`));
  return `${f(from)} – ${f(to)}`;
}

const withYear = new Intl.DateTimeFormat('es-ES', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  timeZone: 'UTC',
});

/**
 * Período de facturación legible: `2026-09-23`, `2026-10-22` → `23 sept – 22 oct 2026` (el año una
 * sola vez si coincide; si cruza de año, en las dos puntas).
 */
export function fmtPeriod(from: string | null | undefined, to: string | null | undefined): string {
  if (!from || !to) return '—';
  const a = new Date(`${from.slice(0, 10)}T00:00:00Z`);
  const b = new Date(`${to.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return '—';
  return a.getUTCFullYear() === b.getUTCFullYear()
    ? `${shortDate.format(a)} – ${withYear.format(b)}`
    : `${withYear.format(a)} – ${withYear.format(b)}`;
}

/** Lee un archivo del input como base64 (sin el prefijo `data:`). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(new Error('No se pudo leer el archivo'));
    };
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}

/** Etiqueta en español de los roles. */
export const ROLE_LABELS: Record<string, string> = {
  owner: 'Dueño (agencia)',
  admin: 'Admin (agencia)',
  operator: 'Operador',
  viewer: 'Lectura (agencia)',
  client_admin: 'Admin de la empresa',
  client_viewer: 'Lectura',
};
